import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Stack,
  Group,
  Button,
  PageHeader,
  Paper,
  FormField,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Alert,
  Loader,
  Text,
  AsyncSearchSelect,
  ProductImageManager,
  BottomDrawer,
  type AsyncSearchPageResult,
  type ManagedProductImage,
} from '@vipso/shared';
import {
  useGetProductQuery,
  useAddUpdateProductMutation,
  useLazyListCategoriesQuery,
  useLazyListUnitsQuery,
  useAddUpdateCategoryMutation,
  useAddUpdateUnitMutation,
} from '../api/tenantApi';
import { uploadFile } from '../api/uploadFile';
import type { RootState } from '../store';
import { useModuleTerminology } from '../hooks/useModuleLabel';
import { useModuleAccess } from '../hooks/useModuleAccess';
import { useFileViewer } from '../features/files/FileViewerContext';

type FormState = {
  name: string;
  unit: string;
  categoryId: string;
  categoryName: string;
  description: string;
  quantity: number;
  pricePerQuantity: number;
  showSubse: boolean;
  addOnItem: boolean;
  addOnItemName: string;
  images: ManagedProductImage[];
};

const emptyForm = (): FormState => ({
  name: '',
  unit: '',
  categoryId: '',
  categoryName: '',
  description: '',
  quantity: 0,
  pricePerQuantity: 0,
  showSubse: true,
  addOnItem: false,
  addOnItemName: '',
  images: [],
});

export function ProductFormPage() {
  const { t } = useTranslation();
  const { openFileViewer } = useFileViewer();
  const terms = useModuleTerminology('product');
  const access = useModuleAccess('product');
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const accessToken = useSelector((state: RootState) => state.auth.accessToken);
  const allowed = isEdit ? access.canUpdate : access.canCreate;

  const { data, isLoading, isError } = useGetProductQuery(id!, { skip: !isEdit || !allowed });
  const [save, { isLoading: saving }] = useAddUpdateProductMutation();
  const [fetchCategories] = useLazyListCategoriesQuery();
  const [fetchUnits] = useLazyListUnitsQuery();
  const [addCategory] = useAddUpdateCategoryMutation();
  const [addUnit] = useAddUpdateUnitMutation();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [hydrated, setHydrated] = useState(!isEdit);
  const [quickAdd, setQuickAdd] = useState<'unit' | 'category' | null>(null);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [quickAddError, setQuickAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEdit || hydrated || !data?.getProduct) return;
    const p = data.getProduct;
    const gallery: ManagedProductImage[] =
      p.images && p.images.length
        ? p.images.map((img) => ({
            fileId: String(img.fileId),
            url: img.url ?? '',
            isMain: Boolean(img.isMain),
          }))
        : p.imageFileId
          ? [
              {
                fileId: String(p.imageFileId),
                url: p.imageUrl ?? '',
                isMain: true,
              },
            ]
          : [];
    if (gallery.length && !gallery.some((g) => g.isMain)) {
      gallery[0] = { ...gallery[0]!, isMain: true };
    }
    setForm({
      name: p.name ?? '',
      unit: p.unit ?? '',
      categoryId: p.categoryId ? String(p.categoryId) : p.category?.id ? String(p.category.id) : '',
      categoryName: p.category?.name ?? '',
      description: p.description ?? '',
      quantity: Number(p.quantity ?? 0),
      pricePerQuantity: Number(p.pricePerQuantity ?? 0),
      showSubse: Boolean(p.showSubse),
      addOnItem: Boolean(p.addOnItem),
      addOnItemName: p.addOnItemName ?? '',
      images: gallery,
    });
    setHydrated(true);
  }, [isEdit, hydrated, data?.getProduct]);

  function patch(partial: Partial<FormState>) {
    setForm((prev) => ({ ...prev, ...partial }));
  }

  const loadUnits = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const res = await fetchUnits({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = res.listUnits?.rows ?? [];
      return {
        rows: rows.map((u) => ({ value: u.name, label: u.name })),
        total: res.listUnits?.total ?? 0,
      };
    },
    [fetchUnits],
  );

  const loadCategories = useCallback(
    async (args: { search: string; page: number; pageLimit: number }): Promise<AsyncSearchPageResult> => {
      const res = await fetchCategories({
        search: args.search || undefined,
        page: args.page,
        pageLimit: args.pageLimit,
      }).unwrap();
      const rows = res.listCategories?.rows ?? [];
      return {
        rows: rows.map((c) => ({ value: c.id, label: c.name })),
        total: res.listCategories?.total ?? 0,
      };
    },
    [fetchCategories],
  );

  async function createUnitFromSearch(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await addUnit({ name: trimmed }).unwrap();
    patch({ unit: created.addUpdateUnit.name });
  }

  async function createCategoryFromSearch(name: string) {
    const trimmed = name.trim();
    if (!trimmed) return;
    const created = await addCategory({ name: trimmed }).unwrap();
    patch({
      categoryId: created.addUpdateCategory.id,
      categoryName: created.addUpdateCategory.name,
    });
  }

  function openQuickAdd(kind: 'unit' | 'category') {
    setQuickAdd(kind);
    setQuickAddName('');
    setQuickAddError(null);
  }

  async function handleQuickAddSave() {
    const trimmed = quickAddName.trim();
    if (!trimmed || !quickAdd) return;
    setQuickAddSaving(true);
    setQuickAddError(null);
    try {
      if (quickAdd === 'unit') {
        await createUnitFromSearch(trimmed);
      } else {
        await createCategoryFromSearch(trimmed);
      }
      setQuickAdd(null);
      setQuickAddName('');
    } catch (err) {
      const message =
        err && typeof err === 'object' && 'data' in err
          ? String((err as { data?: { message?: string } }).data?.message ?? '')
          : '';
      setQuickAddError(message || t('common.saveFailed'));
    } finally {
      setQuickAddSaving(false);
    }
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = t('products.validation.name');
    if (!form.unit.trim()) next.unit = t('products.validation.unit');
    if (!form.categoryId) next.categoryId = t('products.validation.category');
    if (form.addOnItem && !form.addOnItemName.trim()) {
      next.addOnItemName = t('products.validation.addonName');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit() {
    setSubmitError('');
    if (!validate()) return;

    const main = form.images.find((img) => img.isMain) ?? form.images[0];
    const payload: Record<string, unknown> = {
      name: form.name.trim(),
      unit: form.unit.trim(),
      categoryId: Number(form.categoryId),
      description: form.description.trim() || null,
      quantity: Number(form.quantity) || 0,
      pricePerQuantity: Number(form.pricePerQuantity) || 0,
      showSubse: form.showSubse,
      addOnItem: form.addOnItem,
      addOnItemName: form.addOnItem ? form.addOnItemName.trim() : null,
      imageFileIds: form.images.map((img) => Number(img.fileId)),
      mainImageFileId: main ? Number(main.fileId) : null,
      imageFileId: main ? Number(main.fileId) : null,
    };
    if (isEdit && id) payload.id = id;

    try {
      const result = await save(payload).unwrap();
      const savedId = result.addUpdateProduct.id;
      navigate(`/products/${savedId}`, { replace: true });
    } catch (e: unknown) {
      const msg =
        (e as { data?: { message?: string }; message?: string })?.data?.message ??
        (e as { message?: string })?.message ??
        t('products.saveFailed');
      setSubmitError(msg);
    }
  }

  if (!allowed) {
    return <Navigate to="/products" replace />;
  }

  if (isEdit && ((isLoading && !data?.getProduct) || !hydrated) && !isError) {
    return (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  }

  if (isEdit && (isError || !data?.getProduct)) {
    return (
      <Alert color="red" title={t('products.notFound')}>
        <Button variant="light" mt="sm" onClick={() => navigate('/products')}>
          {t('products.backToList')}
        </Button>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader
        title={isEdit ? t('terminology.edit', { entity: terms.singular }) : t('terminology.add', { entity: terms.singular })}
        description={isEdit ? t('products.editDescription') : t('products.addDescription')}
        actions={
          <Button variant="default" onClick={() => navigate(isEdit && id ? `/products/${id}` : '/products')}>
            {t('common.cancel')}
          </Button>
        }
      />

      {submitError ? (
        <Alert color="red" title={t('common.saveFailed')}>
          {submitError}
        </Alert>
      ) : null}

      <Paper p="md" withBorder radius="md">
        <Stack gap="md">
          <ProductImageManager
            label={t('products.images')}
            setMainLabel={t('products.setMainImage')}
            addLabel={t('products.addImages')}
            images={form.images}
            onChange={(images) => patch({ images })}
            onUpload={async (file) => {
              if (!accessToken) throw new Error(t('auth.notSignedIn'));
              return uploadFile(file, accessToken);
            }}
            onImageClick={(image, index) => {
              const siblings = form.images.map((img) => img.fileId);
              openFileViewer({
                fileId: image.fileId,
                siblings,
                siblingIndex: index,
              });
            }}
          />

          <FormField label={t('products.namePlaceholder')} required error={errors.name}>
            <TextInput
              value={form.name}
              placeholder={t('products.namePlaceholder')}
              onChange={(e) => patch({ name: e.currentTarget.value })}
            />
          </FormField>

          <FormField label={t('products.unit')} required error={errors.unit}>
            <AsyncSearchSelect
              placeholder={t('products.unitSearch')}
              value={form.unit}
              valueLabel={form.unit}
              onChange={(value, label) => patch({ unit: label || value })}
              fetchPage={loadUnits}
            />
            <Group gap="xs" mt={6}>
              <Text size="xs" c="dimmed">
                {t('products.addUnitHint')}
              </Text>
              <Button size="xs" variant="light" onClick={() => openQuickAdd('unit')}>
                {t('products.addUnit')}
              </Button>
            </Group>
          </FormField>

          <FormField label={t('products.category')} required error={errors.categoryId}>
            <AsyncSearchSelect
              placeholder={t('products.categorySearch')}
              value={form.categoryId}
              valueLabel={form.categoryName}
              onChange={(value, label) => patch({ categoryId: value, categoryName: label })}
              fetchPage={loadCategories}
            />
            <Group gap="xs" mt={6}>
              <Text size="xs" c="dimmed">
                {t('products.missingCategory')}
              </Text>
              <Button size="xs" variant="light" onClick={() => openQuickAdd('category')}>
                {t('products.addCategory')}
              </Button>
            </Group>
          </FormField>

          <FormField label={t('products.productDescription')}>
            <Textarea
              minRows={3}
              value={form.description}
              placeholder={t('products.descriptionPlaceholder')}
              onChange={(e) => patch({ description: e.currentTarget.value })}
            />
          </FormField>

          <Group grow align="flex-start">
            <FormField label={t('products.quantity')}>
              <NumberInput
                min={0}
                decimalScale={3}
                value={form.quantity}
                onChange={(val) => patch({ quantity: Number(val) || 0 })}
              />
            </FormField>
            <FormField label={t('products.pricePerQuantityLabel')}>
              <NumberInput
                min={0}
                decimalScale={2}
                value={form.pricePerQuantity}
                onChange={(val) => patch({ pricePerQuantity: Number(val) || 0 })}
              />
            </FormField>
          </Group>

          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              {t('products.subsequentQuantity')}
            </Text>
            <Switch
              checked={form.showSubse}
              onChange={(e) => patch({ showSubse: e.currentTarget.checked })}
              label={form.showSubse ? t('common.on') : t('common.off')}
              labelPosition="left"
            />
          </Group>

          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              {t('products.addOnItem')}
            </Text>
            <Switch
              checked={form.addOnItem}
              onChange={(e) =>
                patch({
                  addOnItem: e.currentTarget.checked,
                  addOnItemName: e.currentTarget.checked ? form.addOnItemName : '',
                })
              }
              label={form.addOnItem ? t('common.on') : t('common.off')}
              labelPosition="left"
            />
          </Group>

          {form.addOnItem ? (
            <FormField label={t('products.addOnItemName')} required error={errors.addOnItemName}>
              <TextInput
                value={form.addOnItemName}
                placeholder={t('products.addOnPlaceholder')}
                onChange={(e) => patch({ addOnItemName: e.currentTarget.value })}
              />
            </FormField>
          ) : null}

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => navigate(isEdit && id ? `/products/${id}` : '/products')}>
              {t('common.cancel')}
            </Button>
            <Button loading={saving} onClick={() => void handleSubmit()}>
              {isEdit ? t('products.update') : t('products.save')}
            </Button>
          </Group>
        </Stack>
      </Paper>

      <BottomDrawer
        opened={quickAdd != null}
        onClose={() => setQuickAdd(null)}
        title={
          quickAdd === 'unit' ? t('products.addUnit') : t('products.addCategory')
        }
      >
        <Stack gap="md">
          {quickAddError ? (
            <Alert color="red" title={t('common.saveFailed')}>
              {quickAddError}
            </Alert>
          ) : null}
          <FormField
            label={t('common.name')}
            required
            error={!quickAddName.trim() && quickAddSaving ? t('products.validation.name') : undefined}
          >
            <TextInput
              value={quickAddName}
              placeholder={
                quickAdd === 'unit'
                  ? t('products.newUnitPrompt')
                  : t('products.newCategoryPrompt')
              }
              onChange={(e) => setQuickAddName(e.currentTarget.value)}
              autoFocus
            />
          </FormField>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setQuickAdd(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={quickAddSaving}
              disabled={!quickAddName.trim()}
              onClick={() => void handleQuickAddSave()}
            >
              {t('common.save')}
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>
    </Stack>
  );
}
